import { Component, OnInit, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GridUtilitiesService, IPatientHistory, ImportIconRendererComponent } from '@se/platform';
import { Logger, NotificationService, DataBinding } from '@se/se.ui.html5';
import { GridOptions, ColDef, GridReadyEvent, GridApi, GridSizeChangedEvent, CellStyle, ITooltipParams } from 'ag-grid-community';
import { isEmpty, isObject } from 'lodash-es';
import { Subscription, filter } from 'rxjs';

import * as fromCp from '../../../store/cp.state';
import { selectCpDialogVisible } from '../../../store/cp.selectors';
import { PropertyType, IImportDataBinding } from '../../../cp-configuration/view-model';
import { CpDialogService } from '../../../cp-dialog-service';
import { CpImportService } from '../import.service';

@Component({
    selector: 'cp-import-grid',
    templateUrl: './import-grid.component.html'
})
export class CpImportGridComponent implements OnInit, OnDestroy {

    /*
     * 1. Viene chiamato un metodo REST per recuperare il pregresso
     * 2. Visualizza (in un popup?) una griglia con il pregresso
     *
     * Data/Ora ---
     * Esame --
     * Modulo(?) --
     * Pulsante --
     * Preview dati (una colonna per ogni binding:
     *      se è un guidArray viene visualizzata la lista delle description,
     *      se è un oggetto viene visualizzata un'icona)
     *
     * Il click sul pulsante aggiunge i dati al modello.
     */

    patientHistoryList: IPatientHistory[] = [];
    dataBindings!: IImportDataBinding[];

    gridOptions: GridOptions = {};
    columnDefs = [];

    private gridApi?: GridApi;
    private storeSubscription?: Subscription;

    private readonly hiddenStyle = { display: 'none' };
    private readonly buttonStyle = {
        display: 'inherit',
        padding: '0 0 0 10px'
    };

    constructor(
        private importService: CpImportService,
        private cpDialogService: CpDialogService,
        private translate: TranslateService,
        private store: Store<fromCp.RootState>,
        private logger: Logger,
        private notificationService: NotificationService,
        private gridUtils: GridUtilitiesService) { }

    ngOnInit(): void {

        if (!this.cpDialogService.param)
            throw new Error("This component is intended to be placed into a modal dialog.");

        this.dataBindings = this.cpDialogService.param.dataBindings;
        this.patientHistoryList = this.cpDialogService.param.patientHistoryList;

        if (!this.dataBindings || isEmpty(this.dataBindings))
            throw new Error("Missing binding for import component.");

        this.storeSubscription = this.store
            .pipe(
                select(selectCpDialogVisible),
                filter((isVisible) => isVisible)
            )
            .subscribe(() => this.onCpDialogVisibleChange());

        this.initGrid();
    }

    ngOnDestroy() {
        this.storeSubscription?.unsubscribe();
    }

    // #region grid events

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.fitColumnIfNeed(this.gridApi);
    }

    onGridSizeChanged(_: GridSizeChangedEvent) {
        this.fitColumnIfNeed(this.gridApi!);
        this.gridApi?.refreshCells({force: true});
    }

    onColumnResized() {
        this.gridApi?.resetRowHeights();
    }

    // #endregion

    callbackImport(data: Partial<Record<string, PropertyType>>) {

        try {
            this.importService.updateBindingTarget(this.dataBindings, data);
        }
        catch (e) {
            if (e instanceof Error) {
                this.logger?.error(e.message);
            }
            this.notificationService.error(this.translate.instant('ErrorLoadingPatientHistory'));
        }

        this.cpDialogService.close();
    }

    // #region privates

    //ogni volta che la dialog è visibile refresho la variabile patientHistoryList e le colonne
    //perchè potrei averla cambiate (posso avere più pulsanti di import nella stessa pagina)
    private onCpDialogVisibleChange() {
        if (!this.cpDialogService.param)
            throw new Error('This component is intended to be placed into a modal dialog.');

        this.dataBindings = this.cpDialogService.param.dataBindings;
        this.patientHistoryList = this.cpDialogService.param.patientHistoryList;

        if (!this.dataBindings || isEmpty(this.dataBindings)) {
            throw new Error('Missing binding for import component.');
        }

        const columnDefs = this.createColumns();
        this.gridApi?.updateGridOptions({
            columnDefs,
        });
    }

    private createColumns(): ColDef[] {
        const colDefs: ColDef[] = [
            {
                colId: 'btnImport',
                headerName: '',
                tooltipValueGetter: (_) => this.translate.instant('Import'),
                width: 60,
                valueGetter: (_) => '',
                suppressSizeToFit: true,
                resizable: false,
                lockPosition: true,
                pinned: 'right',
                cellStyle: (p) => this.importButtonStyle(p.data),
                cellRenderer: ImportIconRendererComponent,
            },
        ];

        this.dataBindings.forEach(b => {
            const colToAdd: ColDef = {
                // TODO: Binding avanzato.
                headerName: isEmpty(b.gridHeader) ?  this.translate.instant(b.dataBindingSource.propertyName) : b.gridHeader,
                valueGetter: p => this.fromDataPresenterToColString(p.data, b.dataBindingSource, b.gridVisibleProp),
                tooltipValueGetter: p => this.fromDataPresenterToTooltip(p),
                tooltipComponent: 'tooltipComp',
                maxWidth:400,
                minWidth: 120,
                sortable: false,
                cellStyle: p => this.dataPresenterStyle(p.data, b.dataBindingSource, b.gridVisibleProp)
            };

            colDefs.push(colToAdd);
        });

        return colDefs;
    }

    private importButtonStyle(data: IPatientHistory) {

        //mi basta una cella non ancora caricata o in errore per disabilitare l'import su quella riga

        const dataValidator = this.importService.validateData(data, this.dataBindings);
        if (dataValidator.isEmpty || dataValidator.onError)
            return this.hiddenStyle;

        return this.buttonStyle;
    }

    // #region imported data cells

    private dataPresenterStyle(data: IPatientHistory, dataBinding: DataBinding, renderedProperty?: string): CellStyle {
        let cellContent = this.importService.dataPresenter(data, dataBinding, renderedProperty);

        if (isObject(cellContent)) {
            //se è un oggetto renderizzo un'icona
            return {
                fontFamily: 'Material Icons',
                fontSize: '24px',
                textAlign: 'center'
            };
        }

        cellContent = cellContent?.toLowerCase() ?? '';

        if (cellContent.startsWith(this.translate.instant('ImportNoData').toLowerCase())) {
            return { color: 'red' };
        }

        if (cellContent.startsWith('loading...')) {
            return { color: 'blue' };
        }

        return { color: 'inherit' };
    }

    private fromDataPresenterToColString(data: IPatientHistory, dataBinding: DataBinding, renderedProperty?: string): string | null {
        const dataPresenter = this.importService.dataPresenter(data, dataBinding, renderedProperty);

        if (isObject(dataPresenter))
            return "list_alt"; //se è un oggetto visualizziamo un'icona

        return dataPresenter;
    }

    private fromDataPresenterToTooltip(params: ITooltipParams<any, any>): string | null {
        if (params.value === 'list_alt') {
            return null;
        }

        return params.value;
    }

    // #endregion

    private initGrid() {
        this.gridOptions = this.importService.getBaseGridOptions();
        this.gridOptions.columnDefs?.push(...this.createColumns());
        this.gridOptions.context.callbackImport = this.callbackImport.bind(this);
    }

    private fitColumnIfNeed(columnApi: GridApi) {
        const columns = columnApi.getColumns()?.slice(0, -1);
        if (columns) {
            columnApi.autoSizeColumns(columns);
        }
    }

    // #endregion
}
